<template>
  <b-modal
    v-model="isOpen"
    has-modal-card
    trap-focus
    scroll="keep"
    aria-role="dialog"
    aria-label="EventModale"
    :aria-modal="true"
    class="EventModale"
    @cancel="() => emitCloseModale(false)"
  >
    <div class="modal-card">
      <ValidationObserver ref="observer" v-slot="{ handleSubmit }">
        <header class="modal-card-head modal-card-head-event">
          <button
            type="button"
            class="delete"
            aria-label="close"
            @click="() => emitCloseModale(false)"
          ></button>
          <div class="event">
            {{ $t("eventModale.title") }} <br />
            <div class="event-line" />
          </div>
        </header>

        <section class="modal-card-body">
          <div class="columns">
            <div class="column">
              <DatePickerWithValidation
                v-model="selectedDate"
                vid="event-date"
                rules="required"
                :label="$t('eventModale.date')"
                :placeholder="$t('eventModale.select-date')"
                :min-date="minDate"
                :max-date="maxDate"
                :unselectable-dates="unselectableDates"
              />
            </div>
          </div>
          <div class="columns">
            <div class="column">
              <InputWithValidation
                v-model="content"
                vid="event-content"
                rules="required"
                type="textarea"
                :label="$t('eventModale.content')"
                :placeholder="$t('eventModale.enter-content')"
                maxlength="140"
              />
            </div>
          </div>
        </section>

        <footer class="modal-card-foot">
          <div class="buttons">
            <div class="hoverable-discrete-link mr-3" @click="emitCloseModale(false)">
              {{ $t("annuler") }}
            </div>
            <b-button
              v-if="isPorteurProjet"
              rounded
              type="is-primary"
              @click="handleSubmit(addAndClose)"
            >
              {{ $t("eventModale.add") }}
            </b-button>
          </div>
        </footer>
      </ValidationObserver>
    </div>
  </b-modal>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import { CalendrierEvenementCreateDTO, ProjetAnnuelDTO } from "@/model/bean/GeneratedDTOs"
import DatePickerWithValidation from "@/views/inputs/DatePickerWithValidation.vue"
import InputWithValidation from "@/views/inputs/InputWithValidation.vue"
import { EvenementService } from "@/services/rest/evenement/EvenementService"
import ConversionUtils from "@/services/ConversionUtils"
import ValidationObserver from "@/services/validation/ValidationObserver.vue"
import { BButton, BModal } from "buefy"

export default defineComponent({
  name: "EventModale",
  components: {
    DatePickerWithValidation,
    InputWithValidation,
    ValidationObserver,
    BButton,
    BModal,
  },
  props: {
    projetAnnuel: {
      type: Object as PropType<ProjetAnnuelDTO>,
      required: true,
    },
    open: {
      type: Boolean,
      required: true,
    },
    isPorteurProjet: {
      type: Boolean,
      default: false,
    },
    unselectableDates: {
      type: Array as PropType<Date[]>,
      default: () => [],
    },
  },

  emits: ["close-modale"],
  data() {
    return {
      evenementService: EvenementService.INSTANCE,
      selectedDate: undefined as Date | undefined,
      minDate: new Date(),
      maxDate: new Date(),
      content: "",
    }
  },
  computed: {
    isOpen: {
      get(): boolean {
        return this.open
      },
      set(value: boolean): void {
        if (!value) {
          this.emitCloseModale(false)
        }
      },
    },
  },
  watch: {
    unselectableDates(): void {
      const dates = this.unselectableDates.map((date) => date.getTime())
      this.selectedDate?.setHours(0, 0, 0, 0)
      while (dates.includes(this.selectedDate ? this.selectedDate.getTime() : 0)) {
        this.selectedDate?.setDate(this.selectedDate.getDate() - 1)
      }
    },
  },
  mounted(): void {
    if (this.projetAnnuel) {
      this.minDate = new Date(this.projetAnnuel.anneeScolaireDebut)
      this.maxDate = new Date(this.projetAnnuel.anneeScolaireFin)
    }
  },
  methods: {
    async addAndClose(): Promise<void> {
      if (this.projetAnnuel && this.projetAnnuel.id && this.selectedDate) {
        const evenement = {
          dateEvenement: ConversionUtils.convertToUTCDate(this.selectedDate),
          contenu: this.content,
        } as CalendrierEvenementCreateDTO
        this.evenementService.createEvenement(this.projetAnnuel.id, evenement).then(() => {
          this.resetForm()
          this.emitCloseModale(true)
        })
      }
    },

    resetForm(): void {
      this.selectedDate = new Date()
      this.content = ""
    },

    emitCloseModale(withRefresh: boolean): void {
      this.$emit("close-modale", withRefresh)
    },
  },
})
</script>

<style lang="scss">
@import "@/styles/variables";

.title-without-margin {
  .title-view {
    margin-top: 0;
  }
}

.EventModale {
  &.modal .modal-card {
    height: 75%;
    overflow-y: auto !important;
  }

  .animation-content {
    height: 85%;
  }
}

.modal-card-head-event {
  display: flex;
  background-color: white !important;

  .event {
    color: $ofb-bleu-2;
    font-family: $font-family-title;
    font-size: 26px;
    padding-right: 15px;
  }

  .event-line {
    width: 70px;
    height: 3px;
    background-color: $ofb-bleu-2;
  }
}
</style>
